import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

import withRouter from "../../components/Common/withRouter";

// API
import { post } from "../../api/axios";
import endpoints from "../../api/endpoints";

// Utils
import { extractErrorMessage } from "../../utils/errorHandler";
import { useRecaptcha, loadRecaptchaScript } from "../../utils/recaptcha";

// Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

// reusable components
import CustomInput from "../../components/AuthenticationComponents/CustomInput";
import CustomButton from "../../components/AuthenticationComponents/CustomButton";
import LegalModal from "../../components/AuthenticationComponents/LegalModal";

// Legal content
import PrivacyContent from "../../components/AuthenticationComponents/PrivacyContent";
import TermsContent from "../../components/AuthenticationComponents/TermsContent";
import logo from "../../assets/images/authentication/logo-auth.svg";
import "../../assets/scss/custom/pages/Authentication.scss";
import "../../assets/scss/custom/pages/LegalPages.scss";
import zapIcon from "../../assets/images/authentication/zap.svg";
import creditCardIcon from "../../assets/images/authentication/credit-card.svg";
import giftIcon from "../../assets/images/authentication/gift.svg";
import googleIcon from "../../assets/images/authentication/google-icon.svg";
import arrowRightIcon from "../../assets/images/authentication/arrow-right.svg";

const Register = (props) => {
  document.title = "Register | Omaha Food Lovers";

  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      password_confirmation: "",
      referral_code: "",
    },
    validationSchema: Yup.object({
      first_name: Yup.string()
        .trim()
        .required("Please enter your first name")
        .min(2, "First name must be at least 2 characters")
        .max(50, "First name cannot exceed 50 characters")
        .matches(
          /^[a-zA-Z\s'-]+$/,
          "First name can only contain letters, spaces, hyphens, and apostrophes",
        ),
      last_name: Yup.string()
        .trim()
        .required("Please enter your last name")
        .min(2, "Last name must be at least 2 characters")
        .max(50, "Last name cannot exceed 50 characters")
        .matches(
          /^[a-zA-Z\s'-]+$/,
          "Last name can only contain letters, spaces, hyphens, and apostrophes",
        ),
      email: Yup.string()
        .trim()
        .email("Please enter a valid email")
        .required("Please enter your email")
        .max(255, "Email cannot exceed 255 characters")
        .matches(
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          "Please enter a valid email format",
        ),
      password: Yup.string()
        .trim()
        .min(8, "Password must be at least 8 characters")
        .max(128, "Password cannot exceed 128 characters")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/[0-9]/, "Password must contain at least one number")
        .matches(
          /[^A-Za-z0-9]/,
          "Password must contain at least one special character",
        )
        .required("Please enter your password"),
      password_confirmation: Yup.string()
        .trim()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Please confirm your password"),
      referral_code: Yup.string()
        .trim()
        .test(
          "referral-code-format",
          "Referral code must start with OFL- followed by 5 uppercase letters or numbers (e.g., OFL-A1B2C)",
          (value) => {
            if (!value) return true; // Allow empty since it's optional
            return /^OFL-[A-Z0-9]{5}$/.test(value);
          },
        ),
    }),
    onSubmit: async (values) => {
      setError("");

      // Fetch a fresh reCAPTCHA v3 token right before the API call
      const recaptchaToken = await getToken("register");
      if (!recaptchaToken) return; // captchaError is already set

      setLoading(true);
      try {
        const response = await post(endpoints.signup, {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          password: values.password,
          password_confirmation: values.password_confirmation,
          referral_code: values.referral_code,
          recaptcha_token: recaptchaToken,
        });

        if (response.data.success) {
          // Store token in localStorage
          localStorage.setItem("accessToken", response.data.data.token);
          localStorage.setItem("user", JSON.stringify(response.data.data));

          // Show success toast
          toastr.success(response.data.message || "Registration successful!");

          // Navigate to email verification
          props.router.navigate("/email-verify");
        }
      } catch (error) {
        const errorMessage = extractErrorMessage(error);
        setError(errorMessage);
        toastr.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  const [loading, setLoading] = React.useState(false);
  const [googleLoading, setGoogleLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const {
    getToken,
    executing: captchaExecuting,
    error: captchaError,
  } = useRecaptcha();

  useEffect(() => {
    document.body.className = "authentication-bg";
    window.scrollTo(0, 0);
    // Preload reCAPTCHA so the v3 badge is visible and submit is instant
    loadRecaptchaScript().catch(() => {});

    return function cleanup() {
      document.body.className = "";
    };
  }, []);

  // Auto-populate referral code from URL query parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get("ref");
    if (refCode) {
      validation.setFieldValue("referral_code", refCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const googleLogin = useGoogleLogin({
    onSuccess: async (credentialResponse) => {
      setGoogleLoading(true);
      setError("");
      try {
        // Validate the credential response
        if (!credentialResponse?.credential) {
          throw new Error("Invalid Google credential response");
        }

        // Send the Google ID token to backend for authentication
        const response = await post(endpoints.googleLogin, {
          id_token: credentialResponse.credential,
        });

        if (response.data.success) {
          // Store token in localStorage
          localStorage.setItem("accessToken", response.data.data.token);
          localStorage.setItem("user", JSON.stringify(response.data.data));

          // Show success toast
          toastr.success(response.data.message || "Google signup successful!");

          // Check if email is verified
          if (!response.data.data.email_verified_at) {
            // Email not verified, redirect to email verification screen
            props.router.navigate("/email-verify");
          } else {
            // Email verified, redirect to dashboard
            props.router.navigate("/dashboard");
          }
        } else {
          setError(response.data.message || "Google signup failed");
          toastr.error(response.data.message || "Google signup failed");
        }
      } catch (error) {
        const errorMessage = extractErrorMessage(error);
        setError(errorMessage);
        toastr.error(errorMessage);
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: (error) => {
      console.error("Google Login Error:", error);
      setError("Google login failed. Please try again.");
      toastr.error("Google login failed. Please try again.");
    },
  });

  return (
    <React.Fragment>
      <div className="auth-page">
        <div className="auth-page-grid">
          {/* left brand / marketing panel */}
          <div className="auth-brand-panel">
            <div className="auth-brand-panel-inner">
              <Link to="/" className="auth-logo">
                <img
                  className="auth-logo-icon"
                  src={logo}
                  alt="Omaha Food Lovers"
                />
              </Link>

              <h1 className="auth-headline">
                Earn rewards every time
                <br />
                you dine in <span className="auth-headline-accent">Omaha</span>
              </h1>

              <p className="auth-subline">
                Get 5% back at 200+ restaurants.
                <br />
                Track your dining streak. Unlock exclusive perks.
              </p>

              <ul className="auth-feature-list">
                <li>
                  <span className="auth-feature-icon" aria-hidden="true">
                    <img src={creditCardIcon} alt="Credit Card" />
                  </span>
                  <span>
                    Link your card once. Earn 5% back automatically at 200+
                    local restaurants.
                  </span>
                </li>
                <li>
                  <span className="auth-feature-icon" aria-hidden="true">
                    <img src={giftIcon} alt="Gift" />
                  </span>
                  <span>
                    Redeem points for restaurant vouchers. Your rewards stay
                    local, supporting Omaha's dining scene.
                  </span>
                </li>
                <li>
                  <span className="auth-feature-icon" aria-hidden="true">
                    <img src={zapIcon} alt="Zap" />
                  </span>
                  <span>
                    100% free. No subscription, no hidden fees. Just eat and
                    earn.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* right form panel */}
          <div className="auth-form-panel">
            <div className="auth-form-card">
              <div className="auth-form-header">
                <h2>Create your account</h2>
                <p>Join the Omaha Food Lovers community today.</p>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.submitForm();
                  return false;
                }}
              >
                <div className="custom-input-row">
                  <CustomInput
                    label="First Name"
                    name="first_name"
                    placeholder="e.g. Alex"
                    value={validation.values.first_name}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.first_name &&
                      !!validation.errors.first_name
                    }
                    errorText={validation.errors.first_name}
                    className="glass"
                  />
                  <CustomInput
                    label="Last Name"
                    name="last_name"
                    placeholder="e.g. Johnson"
                    value={validation.values.last_name}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.last_name &&
                      !!validation.errors.last_name
                    }
                    errorText={validation.errors.last_name}
                    className="glass"
                  />
                </div>

                <CustomInput
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="alex@omaha.com"
                  value={validation.values.email}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.email && !!validation.errors.email
                  }
                  errorText={validation.errors.email}
                  autoComplete="email"
                  className="glass"
                />

                <div className="custom-input-row">
                  <CustomInput
                    label="Password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    value={validation.values.password}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.password &&
                      !!validation.errors.password
                    }
                    errorText={validation.errors.password}
                    autoComplete="new-password"
                    className="glass"
                  />
                  <CustomInput
                    label="Confirm Password"
                    name="password_confirmation"
                    type="password"
                    placeholder="••••••••"
                    value={validation.values.password_confirmation}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.password_confirmation &&
                      !!validation.errors.password_confirmation
                    }
                    errorText={validation.errors.password_confirmation}
                    autoComplete="new-password"
                    className="glass"
                  />
                </div>

                <CustomInput
                  label="Referral Code"
                  optional
                  name="referral_code"
                  placeholder="Enter code"
                  value={validation.values.referral_code}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.referral_code &&
                    !!validation.errors.referral_code
                  }
                  errorText={validation.errors.referral_code}
                  className="glass"
                />

                {error || captchaError ? (
                  <p className="auth-server-error">
                    {error || captchaError}
                    {String(error).toLowerCase().includes("already") && (
                      <>
                        {" "}
                        <Link to="/login">Try logging in.</Link>
                      </>
                    )}
                  </p>
                ) : null}

                <div className="auth-submit-wrap">
                  <CustomButton
                    type="submit"
                    variant="primary"
                    iconPosition="right"
                    icon={arrowRightIcon}
                    loading={loading || captchaExecuting}
                  >
                    Create Account
                  </CustomButton>
                </div>

                <div className="auth-divider">
                  <span>Or</span>
                </div>

                <CustomButton
                  variant="secondary"
                  iconPosition="right"
                  onClick={googleLogin}
                  icon={googleIcon}
                  loading={googleLoading}
                >
                  Continue with Google
                </CustomButton>

                <p className="auth-login-link">
                  Already have an account? <Link to="/login">Log in</Link>
                </p>
                <p className="auth-terms">
                  By signing up, you agree to our{" "}
                  <button
                    className="auth-terms-link"
                    onClick={() => setShowTermsModal(true)}
                    type="button"
                  >
                    Terms of Service
                  </button>{" "}
                  and{" "}
                  <button
                    className="auth-terms-link"
                    onClick={() => setShowPrivacyModal(true)}
                    type="button"
                  >
                    Privacy Policy
                  </button>
                  .
                </p>
              </form>
            </div>
          </div>
        </div>
        <p className="auth-brand-footer">
          © Omaha Food Lovers {new Date().getFullYear()} - All Rights Reserved.
        </p>
      </div>

      {/* Legal Modals */}
      <LegalModal
        isOpen={showPrivacyModal}
        toggle={() => setShowPrivacyModal(false)}
        title="Privacy Policy"
      >
        <PrivacyContent />
      </LegalModal>

      <LegalModal
        isOpen={showTermsModal}
        toggle={() => setShowTermsModal(false)}
        title="Terms of Service"
      >
        <TermsContent />
      </LegalModal>
    </React.Fragment>
  );
};

export default withRouter(Register);

Register.propTypes = {
  registerUser: PropTypes.func,
  registrationError: PropTypes.any,
  user: PropTypes.any,
};
